import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import LoginActivityModal from "@/components/LoginActivityModal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Building2, UserRound, KeyRound, Eye, Copy, Check, X, Search,
  Link2, RefreshCw, ShieldCheck, AlertTriangle, ArrowLeft,
  History, PauseCircle, PlayCircle, CalendarClock,
} from "lucide-react";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const day = (v) => (v ? String(v).slice(0, 10) : "—");
const TABS = [
  ["Partner logins", Building2],
  ["Partner clients", UserRound],
];

const th = "text-left text-[11px] uppercase tracking-wider text-ink-3";
const tdRow = "border-t";
const rowStyle = { borderColor: "rgba(15,18,38,0.06)" };
const iconBtn = "inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40";

/* One modal for every kind of password reset. `ask.url` decides whose. */
const PasswordModal = ({ ask, onClose }) => {
  const [custom, setCustom] = useState("");
  const [emailIt, setEmailIt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const body = custom.trim() ? { password: custom.trim() } : {};
      if (ask.canEmail && emailIt) body.email_it = true;
      const { data } = await api.post(ask.url, body);
      setResult(data);
      ask.onDone?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not set that password.");
    }
    setBusy(false);
  };

  const copy = async () => {
    await navigator.clipboard?.writeText(`${result.email}  ${result.password}`);
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(15,18,38,0.55)", backdropFilter: "blur(8px)" }}
      onClick={() => !busy && onClose()} data-testid="pwd-modal">
      <div className="glass p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}
        style={{ background: "rgba(255,255,255,0.98)" }}>
        <button type="button" onClick={() => !busy && onClose()} className="absolute top-3 right-3 org-icon-btn" data-testid="pwd-close">
          <X size={15} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(180,83,9,0.12)" }}>
            <KeyRound size={18} style={{ color: "#B45309" }} />
          </div>
          <div>
            <div className="kpi-label">{ask.kind}</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">{result ? "Password set" : ask.title}</h3>
          </div>
        </div>

        {!result ? (
          <>
            <div className="text-sm text-ink-2 mb-4">{ask.blurb}</div>
            <label className="kpi-label mb-1.5 block">New password (optional)</label>
            <input type="text" value={custom} autoFocus disabled={busy}
              onChange={(e) => { setCustom(e.target.value); setError(""); }}
              placeholder="Leave blank to generate a strong one"
              className="w-full px-3 py-2 rounded-xl border outline-none text-sm font-mono"
              style={{ borderColor: "rgba(15,18,38,0.10)", background: "rgba(15,18,38,0.03)" }}
              data-testid="pwd-input" />
            <div className="text-[11px] text-ink-3 mt-1.5">Min 8 chars · ≥1 uppercase · ≥1 number · ≥1 special character.</div>
            {ask.canEmail && (
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="pwd-email-it-label">
                <input type="checkbox" checked={emailIt} onChange={(e) => setEmailIt(e.target.checked)}
                  className="mt-0.5" data-testid="pwd-email-it" />
                <span className="text-[12.5px] text-ink-2">
                  Email it to them from <b>{ask.brandName || "their adviser"}</b> — their name and colour, nothing of ours on it.
                </span>
              </label>
            )}
            {error && (
              <div className="mt-3 chip chip-amber inline-flex items-center gap-2" data-testid="pwd-error">
                <AlertTriangle size={12} /> {error}
              </div>
            )}
            <div className="flex items-center gap-2 mt-5">
              <button type="button" onClick={onClose} disabled={busy} className="btn-secondary !py-2 !px-3 text-sm flex-1">Cancel</button>
              <button type="button" onClick={submit} disabled={busy}
                className="btn-primary !py-2 !px-3 text-sm flex-1 justify-center" data-testid="pwd-submit">
                <KeyRound size={13} /> {busy ? "Setting…" : custom.trim() ? "Set password" : "Generate & set"}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="pwd-result">
            <div className="glass p-3 mb-3" style={{ background: "rgba(0,134,58,0.06)" }}>
              <div className="text-[11px] text-ink-3">{result.email}</div>
              <div className="font-mono text-[15px] font-semibold mt-0.5" data-testid="pwd-value">{result.password}</div>
            </div>
            <div className="text-[12.5px] text-ink-2">
              {result.emailed
                ? "Emailed to them as well. They can sign in with it as-is — no email code needed."
                : result.email_attempted
                  ? "We tried to email it in the firm's name but it wouldn't send — hand it over yourself for now."
                  : ask.handoff || "Not emailed — pass it on yourself."}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button type="button" onClick={copy} className="btn-secondary !py-2 !px-3 text-sm flex-1" data-testid="pwd-copy">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
              </button>
              <button type="button" onClick={onClose} className="btn-primary !py-2 !px-3 text-sm flex-1 justify-center" data-testid="pwd-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Toolbar = ({ q, setQ, count, label, right }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <div className="relative flex-1 min-w-[220px]">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or firm…"
        className="w-full pl-9 pr-3 py-2 rounded-xl border outline-none text-sm"
        style={{ borderColor: "rgba(15,18,38,0.10)", background: "rgba(255,255,255,0.7)" }}
        data-testid="users-search" />
    </div>
    <span className="text-[12px] text-ink-3">{count} {label}</span>
    {right}
  </div>
);

function PartnerLoginsTab({ onReset, onViewAs, onActivity }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/partners/directory/logins").then(({ data }) => setRows(data.logins));
  useEffect(() => { load(); }, []);

  const toggle = async (u, active) => {
    if (!active && !window.confirm(`Switch off ${u.email}? They won't be able to sign in until you switch it back on. Their firm, book and portal are untouched.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/partners/logins/${u.id}/status`, { active });
      toast.success(active ? "Login switched back on" : "Login switched off");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((u) => !s || `${u.name || ""} ${u.email} ${u.partner_name}`.toLowerCase().includes(s));
  }, [rows, q]);

  if (!rows) return <div className="glass p-6 text-sm text-ink-2">Loading partner logins…</div>;
  if (!rows.length) return <div className="glass p-8 text-center text-sm text-ink-2" data-testid="plogins-empty">No partner logins yet — create one from the Partners list, under Manage.</div>;

  return (
    <div className="space-y-3" data-testid="users-tab-partner-logins-panel">
      <Toolbar q={q} setQ={setQ} count={shown.length} label="console logins" />
      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={th}><th className="px-4 py-2.5">Login</th><th>Firm</th><th>Portal</th><th>Sign-in</th><th className="text-right px-4">Password · view · on/off</th></tr></thead>
          <tbody>
            {shown.map((u) => {
              const off = String(u.status || "Active").toLowerCase() === "suspended";
              return (
              <tr key={u.id} className={tdRow} style={rowStyle} data-testid={`plogin-row-${u.email}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold flex items-center gap-2">
                    {u.name || "—"}
                    {u.is_member && (
                      <span className="chip !text-[10px]" style={{ background: "rgba(194,24,91,0.10)", color: "#C2185B", fontWeight: 700 }}
                        title="A BotChainCP member running this firm — they sign in with their member password, managed in Members"
                        data-testid={`plogin-member-based-${u.email}`}>member login</span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-ink-3">{u.email}</div>
                </td>
                <td>
                  <div className="font-medium">{u.partner_name}</div>
                  <span className={`chip ${u.partner_status === "active" ? "chip-emerald" : "chip-amber"}`}>{u.partner_status}</span>
                </td>
                <td>
                  {u.portal_url
                    ? <a href={u.portal_url} target="_blank" rel="noreferrer" className="text-[12.5px] font-mono" style={{ color: "#4060FF" }}>/p/{u.partner_slug}</a>
                    : <span className="text-ink-3">—</span>}
                </td>
                <td>
                  {off
                    ? <span className="chip chip-amber" data-testid={`plogin-off-${u.email}`}>switched off</span>
                    : <span className="text-ink-2 text-[12.5px]">{day(u.last_login_at)}</span>}
                </td>
                <td className="text-right px-4 whitespace-nowrap">
                  {u.is_member ? (
                    <a href="/admin/members" className="text-[12px] font-semibold mr-2" style={{ color: "#C2185B" }}
                      title="This login is their BotChainCP member account — password, access and on/off all live in Members"
                      data-testid={`plogin-manage-in-members-${u.email}`}>Manage in Members →</a>
                  ) : (
                  <>
                  <button type="button" title="Set a new console password"
                    onClick={() => onReset({
                      kind: "Partner console", title: "Reset password", url: `/partners/logins/${u.id}/reset-password`,
                      blurb: `Set a new partner console password for ${u.email}. It's emailed to them too.`,
                      onDone: load,
                    })}
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(180,83,9,0.10)", color: "#B45309" }}
                    data-testid={`reset-pw-${u.email}`}>
                    <KeyRound size={14} />
                  </button>
                  <button type="button" title="View their console — read-only" disabled={off}
                    onClick={() => onViewAs({ id: u.id, email: u.email })}
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
                    data-testid={`view-as-${u.email}`}>
                    <Eye size={14} />
                  </button>
                  <button type="button" title="Recent sign-ins"
                    onClick={() => onActivity({ id: u.id, email: u.email, label: u.name || u.partner_name })}
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(15,18,38,0.06)", color: "#475569" }}
                    data-testid={`activity-${u.email}`}>
                    <History size={14} />
                  </button>
                  <button type="button" disabled={busyId === u.id} title={off ? "Switch this login back on" : "Switch this login off"}
                    onClick={() => toggle(u, off)}
                    className={iconBtn} style={off ? { background: "rgba(0,134,58,0.10)", color: "#00863A" } : { background: "rgba(225,29,72,0.10)", color: "#E11D48" }}
                    data-testid={`toggle-login-${u.email}`}>
                    {off ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
                  </button>
                  </>
                  )}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      <div className="text-[12px] text-ink-3 inline-flex items-center gap-1.5">
        <ShieldCheck size={12} /> Viewing a partner console is read-only — nothing can be saved or published while you're in there.
      </div>
    </div>
  );
}

function ClientsTab({ onReset, onActivity }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const load = () => api.get("/partners/directory/clients").then(({ data }) => setRows(data.clients));
  useEffect(() => {
    load();
    api.get("/partners/directory/statement-runs").then(({ data }) => setLastRun(data.runs?.[0] || null)).catch(() => {});
  }, []);

  const toggle = async (c, active) => {
    if (!active && !window.confirm(`Switch off ${c.name}'s login? Their file, money and statement link stay exactly as they are — they just can't sign in.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/partners/clients/${c.id}/login-status`, { active });
      toast.success(active ? "Login switched back on" : "Login switched off");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((c) => !s || `${c.name || ""} ${c.email} ${c.partner_name} ${c.reference || ""}`.toLowerCase().includes(s));
  }, [rows, q]);

  const copyLink = async (c) => {
    await navigator.clipboard?.writeText(c.statement_url);
    toast.success("Statement link copied");
  };

  const reissue = async (c) => {
    if (!window.confirm(`Re-issue ${c.name}'s statement link? The old URL stops working immediately.`)) return;
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/partners/clients/${c.id}/reissue-link`);
      await navigator.clipboard?.writeText(data.statement_url);
      toast.success("New link issued and copied");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  if (!rows) return <div className="glass p-6 text-sm text-ink-2">Loading clients…</div>;
  if (!rows.length) return <div className="glass p-8 text-center text-sm text-ink-2" data-testid="pclients-empty">No partner clients yet.</div>;

  return (
    <div className="space-y-3" data-testid="users-tab-partner-clients-panel">
      <Toolbar q={q} setQ={setQ} count={shown.length} label="clients" />
      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={th}><th className="px-4 py-2.5">Client</th><th>Firm</th><th>Committed</th><th>Login</th><th className="text-right px-4">Password / link</th></tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className={tdRow} style={rowStyle} data-testid={`pclient-row-${c.email}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold">{c.name || "—"}</div>
                  <div className="text-[11.5px] text-ink-3">{c.email} · {c.reference}</div>
                </td>
                <td className="text-ink-2">{c.partner_name}</td>
                <td>
                  <div className="font-semibold">{usd(c.committed)}</div>
                  <span className={`chip ${c.funded > 0 ? "chip-emerald" : ""}`}>{c.funded > 0 ? `${usd(c.funded)} funded` : c.status}</span>
                </td>
                <td>
                  {c.open_payout_request && (
                    <span className="chip chip-amber mr-1.5" data-testid={`payout-requested-${c.email}`}>payout asked</span>
                  )}
                  {c.has_login
                    ? (c.login_active
                      ? <span className="chip chip-emerald" data-testid={`client-has-login-${c.email}`}>can sign in · {day(c.login_last_in)}</span>
                      : <span className="chip chip-amber" data-testid={`client-has-login-${c.email}`}>switched off</span>)
                    : <span className="chip">no login yet</span>}
                </td>
                <td className="text-right px-4 whitespace-nowrap">
                  <button type="button" title={c.has_login ? "Set a new password" : "Create their login"}
                    onClick={() => onReset({
                      kind: "Client login", title: c.has_login ? "Reset client password" : "Create client login",
                      url: `/partners/clients/${c.id}/login`,
                      blurb: `${c.has_login ? "Set a new password for" : "Create a login for"} ${c.email}. They sign in on ${c.partner_name}'s own site${c.login_url ? ` (${c.login_url})` : ""} and land on their own statement.`,
                      handoff: "Not emailed — pass it to their adviser, or tick the box above to send it in the firm's name.",
                      canEmail: true, brandName: c.partner_name,
                      onDone: load,
                    })}
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(180,83,9,0.10)", color: "#B45309" }}
                    data-testid={`client-login-${c.email}`}>
                    <KeyRound size={14} />
                  </button>
                  <button type="button" onClick={() => copyLink(c)} disabled={!c.statement_url} title="Copy their private statement link"
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
                    data-testid={`client-copy-link-${c.email}`}>
                    <Link2 size={14} />
                  </button>
                  <button type="button" onClick={() => reissue(c)} disabled={busyId === c.id} title="Re-issue the link (old URL stops working)"
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(15,18,38,0.06)", color: "#475569" }}
                    data-testid={`client-reissue-${c.email}`}>
                    <RefreshCw size={14} />
                  </button>
                  <button type="button" disabled={!c.has_login} title="Recent sign-ins"
                    onClick={() => onActivity({ id: c.login_user_id, email: c.login_email, label: c.name })}
                    className={`${iconBtn} mr-1.5`} style={{ background: "rgba(15,18,38,0.06)", color: "#475569" }}
                    data-testid={`activity-${c.email}`}>
                    <History size={14} />
                  </button>
                  <button type="button" disabled={!c.has_login || busyId === c.id}
                    title={c.login_active ? "Switch their login off" : "Switch their login back on"}
                    onClick={() => toggle(c, !c.login_active)}
                    className={iconBtn}
                    style={!c.login_active ? { background: "rgba(0,134,58,0.10)", color: "#00863A" } : { background: "rgba(225,29,72,0.10)", color: "#E11D48" }}
                    data-testid={`toggle-login-${c.email}`}>
                    {c.login_active ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[12px] text-ink-3 inline-flex items-center gap-1.5" data-testid="statement-run-note">
        <CalendarClock size={12} />
        {lastRun?.result
          ? `Monthly statements: ${lastRun.result.month} run sent ${lastRun.result.sent}, ${lastRun.result.failed} failed, ${lastRun.result.skipped} skipped.`
          : "Monthly statements go out on the 1st, in each firm's own name. No run recorded yet."}
      </div>
    </div>
  );
}

export default function AdminPartnerUsers() {
  const { startPartnerView } = useAuth();
  const [tab, setTab] = useState("Partner logins");
  const [ask, setAsk] = useState(null);
  const [activity, setActivity] = useState(null);

  const viewAs = async ({ id, email }) => {
    try {
      await startPartnerView(id);
      window.location.replace("/partner");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || `Could not view as ${email}`);
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-partner-users-page">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 space-y-6">
        <div>
          <a href="/admin/partners" className="text-[12.5px] inline-flex items-center gap-1.5 text-ink-2 hover:text-ink" data-testid="back-to-partners">
            <ArrowLeft size={13} /> Back to Partners
          </a>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3">Partner user management</h1>
          <p className="text-base text-ink-2 mt-2 max-w-[760px]">
            The partner firms who run their own book, and those firms' clients — set a password, hand over a statement
            link, or look at a console through their eyes. Nothing here touches community members.
          </p>
        </div>

        <div className="inline-flex gap-1 p-1 rounded-2xl" style={{ background: "rgba(15,18,38,0.05)" }}>
          {TABS.map(([t, Icon]) => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-xl text-[13px] font-semibold inline-flex items-center gap-2 transition-colors ${tab === t ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/60"}`}
              data-testid={`users-tab-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon size={14} /> {t}
            </button>
          ))}
        </div>

        {tab === "Partner logins" && <PartnerLoginsTab onReset={setAsk} onViewAs={viewAs} onActivity={setActivity} />}
        {tab === "Partner clients" && <ClientsTab onReset={setAsk} onActivity={setActivity} />}
      </div>

      {ask && <PasswordModal ask={ask} onClose={() => setAsk(null)} />}
      {activity && <LoginActivityModal who={activity} onClose={() => setActivity(null)} />}
    </div>
  );
}
